import { DisplaySettingsRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Avatar, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Scrollbar } from 'app/components';
import { SettingsForm } from 'app/pages/Settings';
import { useAsyncAction } from 'app/services';
import { settingsLoad, settingsUpdate } from 'app/store/settings';
import { transportGroupLoad } from 'app/store/transport';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

const Settings = () => {
  const formRef = useRef();
  const { keys } = useSelector((state) => state.settings);

  const { asyncAction: fetchData, isLoading: isFetchLoading, isSuccessful: isFetchSuccessful } = useAsyncAction(settingsLoad);
  const { asyncAction: fetchGroups, isLoading: isGroupsLoading } = useAsyncAction(transportGroupLoad);
  const {
    asyncAction: updateSubmit,
    isLoading: isUpdateLoading,
    isSuccessful: isUpdateSuccessful,
    isError: isUpdateError,
  } = useAsyncAction(settingsUpdate);

  useEffect(() => {
    fetchData();
    fetchGroups();
  }, []);

  return (
    <React.Fragment>
      <Stack
        direction='column'
        alignItems='center'
        sx={(theme) => ({
          maxWidth: '540px',
          height: '100%',
          margin: '0 auto',
          width: '100%',
          padding: theme.spacing(4, 2),
        })}>
        <Stack
          direction='column'
          alignItems='center'
          sx={(theme) => ({
            marginBottom: theme.spacing(3),
            '.MuiAvatar-root': {
              width: '56px',
              height: '56px',
              bgcolor: theme.palette.primary.main,
            },
            '.MuiAvatar-circular': {
              color: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
            },
            '.MuiTypography-root:first-of-type': {
              marginTop: theme.spacing(1),
            },
          })}>
          <Avatar>
            <DisplaySettingsRounded />
          </Avatar>
          <Typography variant='body1'>Настройки приложения</Typography>
        </Stack>
        <Stack
          direction='column'
          alignItems='center'
          sx={(theme) => ({
            width: '100%',
            height: '100%',
            position: 'relative',
            border: '1px solid',
            borderRadius: theme.shape.borderRadius,
            borderColor: theme.palette.divider,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          })}>
          {isFetchLoading && isGroupsLoading && (
            <Stack
              direction='column'
              alignItems='center'
              justifyContent='center'
              sx={(theme) => ({
                width: '100%',
                height: '100%',
                padding: theme.spacing(2),
              })}>
              <CircularProgress size={24} />
            </Stack>
          )}
          {!isFetchLoading && !isGroupsLoading && isFetchSuccessful && (
            <React.Fragment>
              <Stack
                direction='column'
                sx={(theme) => ({
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  form: {
                    padding: theme.spacing(2),
                  },
                })}>
                <Scrollbar>
                  <SettingsForm ref={formRef} defaultValues={keys} onSubmit={updateSubmit} />
                </Scrollbar>
              </Stack>
              <Stack
                direction='column'
                sx={(theme) => ({
                  width: '100%',
                  padding: theme.spacing(2),
                })}>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    '.MuiAlert-root': {
                      width: '100%',
                      marginBottom: theme.spacing(2),
                      borderRadius: theme.shape.borderRadius,
                    },
                  })}>
                  {!isUpdateLoading && isUpdateError && (
                    <Alert severity='error'>
                      Произошла ошибка при изменении данных. Попробуйте снова или обратитесь в техническую поддержку.
                    </Alert>
                  )}
                  {!isUpdateLoading && isUpdateSuccessful && <Alert severity='success'>Данные успешно изменены.</Alert>}
                </Stack>
                <Stack direction='row' justifyContent='flex-end' sx={{ width: '100%' }}>
                  <LoadingButton
                    variant='contained'
                    loading={isUpdateLoading}
                    onClick={() => formRef.current?.handleFormSubmit()}
                    disableElevation>
                    Сохранить изменения
                  </LoadingButton>
                </Stack>
              </Stack>
            </React.Fragment>
          )}
          {!isFetchLoading && !isFetchSuccessful && (
            <Stack
              direction='column'
              alignItems='center'
              justifyContent='center'
              sx={(theme) => ({
                width: '100%',
                height: '100%',
                padding: theme.spacing(2),
              })}>
              <Typography align='center' variant='body1' component='div'>
                Произошла ошибка при загрузке настроек приложения
              </Typography>
              <Typography align='center' color='text.secondary' variant='caption' component='div'>
                Попробуйте перезагрузить страницу или обратитесь в техническую поддержку
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </React.Fragment>
  );
};

export default Settings;
